import React from 'react';
import { Col, Row } from 'react-bootstrap';
import GroupRadioButton from 'components/input/GroupRadioButton';
import SelectInput from 'components/input/SelectInput';
import YesNoSwitch from 'components/input/YesNoSwitch';
import CheckboxInput from 'components/input/CheckboxInput';
import { useAppSelector } from 'app/hooks';

export const DEGREE_FIRST_BACHELORS =
  `First bachelor's degree (Seeking your first bachelor's degree and have not earned a prior bachelor's degree)`;
export const DEGREE_SECOND_BACHELORS =
  `Second bachelor's degree and beyond (e.g. Master's, Teaching Credential, Certificate, Doctoral)`;
export const DEGREE_DUAL_ENROLLMENT =
  `Dual enrollment (Current high school student interested in taking college-level courses at a CSU campus before high school graduation)`;

export const STATUS_GRADUATING_HS =
  `Graduating high school senior or equivalent (with or without college coursework prior to high school graduation)`;
export const STATUS_ADT_TRANSFER =
  `Transferring from a California Community College and earned or planning to earn an Associate Degree for Transfer (AA-T/ AS-T)`;
export const STATUS_OTHER_TRANSFER =
  `Currently attending or have attended a community college, technical college, CSU, or other four-year institution. Community college attendance includes those transferring with or without an associate degree (AA/AS) that is not part of the Associate Degree for Transfer program (AA-T/AS-T.)`;

/** Legacy option labels kept for existing saved answers */
const LEGACY_DEGREE_FIRST_BACHELORS =
  `First Bachelor's Degree (Seeking your first bachelor's degree and have not earned a prior bachelor's degree)`;
const LEGACY_DEGREE_SECOND_BACHELORS =
  `Second Bachelor's Degree and Beyond (e.g. Master's, Teaching Credential, Certificate, Doctoral)`;
const LEGACY_STATUS_GRADUATING_HS =
  `Graduating High School Senior or equivalent (with or without college coursework prior to High School graduation)`;

export const isFirstBachelorsDegree = (value?: string) =>
  value === DEGREE_FIRST_BACHELORS || value === LEGACY_DEGREE_FIRST_BACHELORS;

export const isSecondBachelorsDegree = (value?: string) =>
  value === DEGREE_SECOND_BACHELORS || value === LEGACY_DEGREE_SECOND_BACHELORS;

export const isGraduatingHighSchoolStatus = (value?: string) =>
  value === STATUS_GRADUATING_HS || value === LEGACY_STATUS_GRADUATING_HS;

export const SECOND_DEGREE_GOAL_OPTIONS = [
  `Second bachelor's degree (i.e., already earned a bachelor's degree and are seeking another one)`,
  `Graduate (e.g., Master's, Doctoral) or professional's degree`,
  `Teaching and service credential only (e.g. Single or Multiple Subject, Special Education, PPS, Librarian, Admin, CalStateTEACH)`,
  `Certificate`,
];

interface Props {
  formik: any;
  check?: any;
  isMobile?: boolean;
  disabled?: boolean;
}

const EnrolimentStatus = (props: Props) => {
  let year_plan_college_options = Array.from({ length: 6 }, (_, i) => i + 2025);
  year_plan_college_options = year_plan_college_options.filter((year) => year !== 2026);

  const { isMobile = false, disabled = false } = props;
  const studentAdditionalInfo = useAppSelector(
    (store) => store.authorization.studentAdditionalInfo
  );
  const myCitizenship = useAppSelector((store) => store.basicInformation.myCitizenship);

  const degreeApplyingFor = props.formik.values.csu_info?.degree_applying_for;
  const educationalStatus = props.formik.values.csu_info?.educational_status;
  const secondDegreeGoals: string[] = Array.isArray(
    props.formik.values.csu_info?.second_degree_goals
  )
    ? props.formik.values.csu_info.second_degree_goals
    : [];

  const toggleSecondDegreeGoal = (goal: string, checked: boolean) => {
    const next = checked
      ? [...secondDegreeGoals.filter((item) => item !== goal), goal]
      : secondDegreeGoals.filter((item) => item !== goal);
    props.formik.setFieldValue('csu_info.second_degree_goals', next);
  };

  React.useEffect(() => {
    if (
      studentAdditionalInfo?.year_plan_college_studies &&
      !props.formik.values?.year_plan_college_studies &&
      !myCitizenship
    ) {
      props.formik.setFieldValue(
        'year_plan_college_studies',
        studentAdditionalInfo?.year_plan_college_studies
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Row className="m-0">
      <Col xs={12}>
        <div className="fs-5 p-1">Enrollment Status</div>
      </Col>
      <Col xs={12}>
        <GroupRadioButton
          columns={2}
          onClear={() => props.formik.setFieldValue('current_enrollment_status', '')}
          className="coallition-flag"
          label="What is your current enrollment status?*"
          mdCol={6}
          name="current_enrollment_status"
          values={props.formik.values.current_enrollment_status}
          onChange={props.formik.handleChange}
          options={[
            { value: 'Middle School / Junior High' },
            { value: 'High School / Secondary School' },
            { value: '2-year College (after high school graduation)' },
            { value: '4-year College (after high school graduation)' },
            { value: 'Not Currently Enrolled in School' },
          ]}
          isMissing={!props.formik.values.current_enrollment_status && props.check}
          disabled={disabled}
        />
      </Col>
      <Col xs={12} md={4} className="my-2 coallition-flag">
        <SelectInput
          mdCol={12}
          label="Year entering college"
          name="year_plan_college_studies"
          form={props.formik}
          option={year_plan_college_options}
          values={props.formik.values.year_plan_college_studies}
          onChange={props.formik.handleChange}
          disabled={disabled}
        />
      </Col>
      <Col xs={12} id="SeekingDegree">
        <GroupRadioButton
          mdCol={12}
          className="mt-3 w-100"
          logo="span-csu-logo"
          verticalLayout
          options={[
            DEGREE_FIRST_BACHELORS,
            DEGREE_SECOND_BACHELORS,
            DEGREE_DUAL_ENROLLMENT,
          ]}
          label="Which degree, credential, program or certificate are you applying for?*"
          values={degreeApplyingFor}
          name="csu_info.degree_applying_for"
          onChange={props.formik.handleChange}
          isMissing={!degreeApplyingFor && props.check}
          disabled={disabled}
        />
      </Col>
      <Col xs={12} id="CurrentEducationalStatus">
        {isFirstBachelorsDegree(degreeApplyingFor) && (
          <>
            <GroupRadioButton
              className="mt-3 w-100"
              logo="span-csu-logo"
              mdCol={12}
              verticalLayout
              options={[STATUS_GRADUATING_HS, STATUS_ADT_TRANSFER, STATUS_OTHER_TRANSFER]}
              label="Which of the following best describes your current educational status?*"
              values={educationalStatus}
              name="csu_info.educational_status"
              onChange={props.formik.handleChange}
              isMissing={!educationalStatus && props.check}
              disabled={disabled}
            />
            {isGraduatingHighSchoolStatus(educationalStatus) ? (
              <YesNoSwitch
                className="mt-3"
                logo="span-csu-logo"
                values={props.formik.values.csu_info?.is_take_college_credit}
                name="csu_info.is_take_college_credit"
                onChange={props.formik.handleChange}
                label="Have you earned or are you planning to earn college credit before graduating high school, including through concurrent or dual enrollment courses?*"
                isMissing={!props.formik.values.csu_info?.is_take_college_credit && props.check}
                onClear={() => props.formik.setFieldValue('csu_info.is_take_college_credit', '')}
                disabled={disabled}
              />
            ) : educationalStatus ? (
              <div className="span-csu-logo mt-3">
                GRAD MAP is currently only available to Graduating High School Seniors or equivalent
                seeking their First Bachelor&apos;s Degree
              </div>
            ) : null}
          </>
        )}
        {degreeApplyingFor === DEGREE_DUAL_ENROLLMENT && (
          <div className="mt-3 w-100">
            <div
              className="text-field-light px-1 w-100"
              style={{
                backgroundColor:
                  !props.formik.values.csu_info?.is_understand_dual_enrollment && props.check
                    ? '#FFEBEB'
                    : '',
              }}
            >
              I understand that I have chosen an educational goal indicating my intention to enroll
              at a CSU campus, earning college credit and high school credit concurrently. I
              recognize that this does not constitute an application for admission, as I am not yet
              graduating high school or seeking to apply for a bachelor&apos;s degree program at the
              CSU at this time.*
            </div>
            <CheckboxInput
              setValue={(e) =>
                props.formik.setFieldValue('csu_info.is_understand_dual_enrollment', e)
              }
              mdCol={'12'}
              logo="span-csu-logo"
              label="I understand I am applying as a dual enrollment student to the CSU."
              name="csu_info.is_understand_dual_enrollment"
              values={props.formik.values.csu_info?.is_understand_dual_enrollment}
              onChange={(e) =>
                props.formik.setFieldValue(
                  'csu_info.is_understand_dual_enrollment',
                  e.target.checked
                )
              }
              disabled={disabled}
            />
            <div className="span-csu-logo mt-2">
              GRAD MAP is currently only available to Graduating High School Seniors or equivalent
              seeking their First Bachelor&apos;s Degree
            </div>
          </div>
        )}
        {isSecondBachelorsDegree(degreeApplyingFor) && (
          <div className="mt-3 w-100">
            <div
              className="label-14px pb-1"
              style={{
                backgroundColor:
                  secondDegreeGoals.length === 0 && props.check ? '#FFEBEB' : '',
                fontWeight: 600,
                color: '#374151',
              }}
            >
              Select one or more of the following degree goals.*
            </div>
            {SECOND_DEGREE_GOAL_OPTIONS.map((goal, index) => (
              <CheckboxInput
                key={goal}
                className="w-100 mb-1"
                mdCol={'12'}
                label={goal}
                name={`csu_info.second_degree_goal_${index}`}
                values={secondDegreeGoals.includes(goal)}
                onChange={(e) => toggleSecondDegreeGoal(goal, e.target.checked)}
                disabled={disabled}
              />
            ))}
            <div className="span-csu-logo mt-2">
              GRAD MAP is currently only available to Graduating High School Seniors or equivalent
              seeking their First Bachelor&apos;s Degree
            </div>
          </div>
        )}
      </Col>
    </Row>
  );
};

export default EnrolimentStatus;
